import { CaretSortIcon } from '@radix-ui/react-icons'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useKestrel } from 'features/kestrelViewer'
import { getTagDisplayName } from 'features/kestrelViewer/hooks/useTagOptions'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'

const ANIMATION_DURATION = 200

const fadeInAnimation = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-0.5rem);
  }
  to {
    opacity: 0.95;
    transform: scale(1) translateY(0);
  }
`

const fadeOutAnimation = keyframes`
  from {
    opacity: 0.95;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(-0.5rem);
  }
`

const StyledCarotSort = styled(CaretSortIcon)`
  color: lightgray;
  &:hover {
    color: white;
  }
`

const SortOptionsContainer = styled.div`
  cursor: pointer;
  position: relative;
`

const DropdownMenu = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 12rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.3);
  z-index: 1000;
  overflow: hidden;
  animation: ${({ $isClosing }) =>
      $isClosing ? fadeOutAnimation : fadeInAnimation}
    ${ANIMATION_DURATION}ms ease-out forwards;
`

const DropdownOption = styled.div<{ $isSelected: boolean }>`
  padding: 0.75rem 1rem;
  color: ${({ theme }) => theme.forecast.text1};
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 0.875rem;

  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.forecast.background2 : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background2};
  }

  &:first-child {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  &:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
  }
`

export const AnnotationSortButton: FC = () => {
  const { tagOptions, setAnnotationSortKey, annotationSortKey } = useKestrel()
  const [showOptions, setShowOptions] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const tagKeys = useMemo(() => Object.keys(tagOptions), [tagOptions])

  const closeMenu = () => {
    setIsClosing(true)
    closeTimeoutRef.current = setTimeout(() => {
      setShowOptions(false)
      setIsClosing(false)
    }, ANIMATION_DURATION)
  }

  const openMenu = () => {
    setShowOptions(true)
    setIsClosing(false)
  }

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        showOptions &&
        !isClosing
      ) {
        closeMenu()
      }
    }

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showOptions, isClosing])

  const handleToggleMenu = () => {
    if (showOptions) {
      closeMenu()
    } else {
      openMenu()
    }
  }

  const handleSelectTag = (tagKey: string) => {
    setAnnotationSortKey(tagKey)
    closeMenu()
  }

  return (
    <TooltipWrapper label={`Sort annotations by tag`} side='top'>
      <SortOptionsContainer ref={containerRef}>
        <StyledCarotSort onClick={handleToggleMenu} />
        {showOptions && (
          <DropdownMenu $isClosing={isClosing}>
            {tagKeys.map((tagKey) => (
              <DropdownOption
                key={tagKey}
                $isSelected={annotationSortKey === tagKey}
                onClick={() => handleSelectTag(tagKey)}>
                {getTagDisplayName(tagKey)}
              </DropdownOption>
            ))}
          </DropdownMenu>
        )}
      </SortOptionsContainer>
    </TooltipWrapper>
  )
}
